import { useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Send, Copy, Trash2, Archive, ArchiveRestore, ChevronRight, Tag } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from '../components/ui/Dialog';
import { CampaignGroupStatusBadge, CampaignStatusBadge } from '../components/CampaignStatusBadge';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import { SenderSelect, useSenders } from '../components/AudiencePicker';
import { CampaignCalendar, campaignHref } from '../components/CampaignCalendar';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useDuplicateCampaign } from '../lib/useDuplicateCampaign';
import { groupCampaigns, type CampaignGroup, type CampaignListEntry } from '../lib/campaignGroups';
import { formatDate, formatSendTime } from '../lib/format';
import { cn } from '../lib/utils';
import { blocksToHtml, seedBlocks } from '../lib/blocks';
import { useFooterDefaults } from '../lib/useFooterDefaults';
import {
  ApiError,
  type Campaign,
  type CampaignTagCount,
  type TemplatesListResponse,
} from '../types/api';

type CampaignView = 'liste' | 'kalender';

const VIEWS: { value: CampaignView; label: string }[] = [
  { value: 'liste', label: 'List' },
  { value: 'kalender', label: 'Calendar' },
];

/**
 * Minimal roving-tabindex tab list (WAI-ARIA "Tabs" pattern) — no Tabs
 * primitive exists yet in `components/ui`, and this is the only user so
 * far. Remembers the chosen view in the URL (`?view=kalender`) so a
 * reload or a shared link keeps it.
 */
function ViewTabs({ view, onChange }: { view: CampaignView; onChange: (v: CampaignView) => void }) {
  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number): void {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const nextIndex = (index + (e.key === 'ArrowRight' ? 1 : VIEWS.length - 1)) % VIEWS.length;
    const next = VIEWS[nextIndex];
    if (!next) return;
    onChange(next.value);
    (document.getElementById(`campaign-tab-${next.value}`) as HTMLButtonElement | null)?.focus();
  }

  return (
    <div role="tablist" aria-label="View" className="inline-flex gap-1 rounded-lg bg-bg p-1">
      {VIEWS.map((v, i) => (
        <button
          key={v.value}
          id={`campaign-tab-${v.value}`}
          role="tab"
          type="button"
          aria-selected={view === v.value}
          tabIndex={view === v.value ? 0 : -1}
          className={cn(
            'rounded-md px-3 h-8 text-sm font-medium transition-colors',
            view === v.value ? 'bg-card shadow-sm text-text' : 'text-muted hover:text-text',
          )}
          onClick={() => onChange(v.value)}
          onKeyDown={(e) => onKeyDown(e, i)}
        >
          {v.label}
        </button>
      ))}
    </div>
  );
}

function CreateCampaignDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [chosenSenderId, setChosenSenderId] = useState('');
  // '' = start blank (the block editor with a starter layout).
  const [templateId, setTemplateId] = useState('');
  const senders = useSenders();
  const footerDefaults = useFooterDefaults();
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => apiFetch<TemplatesListResponse>('/templates'),
    enabled: open,
  });
  const templateItems = [...(templates.data?.templates ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  // Defaults to the first active sender; no id is ever hard-coded.
  const senderId = chosenSenderId || senders.data?.items[0]?.id || '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => {
      // From a template the server makes the copy (html and, for a block
      // template, the block model); the campaign never follows the template
      // afterwards.
      if (templateId) {
        return apiFetch<{ campaign: Campaign }>('/campaigns', {
          method: 'POST',
          body: { senderId, name, subject, templateId },
        });
      }
      // Stored together with its block document so the editor can reopen it.
      const blocks = seedBlocks(footerDefaults);
      return apiFetch<{ campaign: Campaign }>('/campaigns', {
        method: 'POST',
        body: { senderId, name, subject, html: blocksToHtml(blocks), blockJson: { blocks } },
      });
    },
    onSuccess: ({ campaign }) => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setOpen(false);
      navigate(`/kampagner/${campaign.id}`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>+ New campaign</Button>
      </DialogTrigger>
      <DialogContent title="New campaign">
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          {mutation.isError ? (
            <Alert>
              {mutation.error instanceof ApiError && mutation.error.body.message
                ? mutation.error.body.message
                : 'The campaign could not be created.'}
            </Alert>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-name">Name (internal)</Label>
            <Input id="new-name" required value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-subject">Subject line</Label>
            <Input
              id="new-subject"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <SenderSelect id="new-sender" value={senderId} onChange={setChosenSenderId} />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-template">Start from</Label>
            <select
              id="new-template"
              className="h-[42px] w-full rounded-[9px] border border-zinc-300 bg-card px-3 text-sm"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              <option value="">Blank (block editor)</option>
              {templateItems.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} - {t.kind === 'html' ? 'HTML' : 'blocks'}
                </option>
              ))}
            </select>
            <p className="m-0 text-xs text-muted">
              {templateId
                ? 'The campaign gets its own copy of the template. Changing the template later does not change this campaign.'
                : 'Pick a template to start from its content, or start blank.'}
            </p>
            {templates.isError ? (
              <p role="alert" className="m-0 text-xs text-danger-text">
                The templates could not be loaded. You can still start blank.
              </p>
            ) : null}
          </div>
          <Button type="submit" disabled={!senderId || mutation.isPending}>
            {mutation.isPending ? 'Creating…' : 'Create draft'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function isCampaignView(v: string | null): v is CampaignView {
  return v === 'liste' || v === 'kalender';
}

/**
 * Under the name: when an approved campaign is due ("Sends ..."), or when a draft is planned
 * for. A time that has passed is not shown for a scheduled campaign (the cron starts it within
 * a minute), and a draft with a passed time says so, since it can no longer be approved.
 */
function sendTimeNote(c: Campaign): string | null {
  if (c.scheduledAt === null) return null;
  if (c.status === 'scheduled' && c.scheduledAt > Date.now()) {
    return `Sends ${formatSendTime(c.scheduledAt)}`;
  }
  if (c.status === 'draft') {
    return c.scheduledAt > Date.now()
      ? `Planned for ${formatSendTime(c.scheduledAt)}`
      : `Planned time has passed (${formatSendTime(c.scheduledAt)})`;
  }
  return null;
}

/** Deletes a draft after a second click; only drafts can be deleted (the API refuses others). */
function DeleteDraftButton({ campaign }: { campaign: Campaign }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const mutation = useMutation({
    mutationFn: () => apiFetch(`/campaigns/${campaign.id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-xs font-medium hover:bg-card disabled:opacity-50',
        confirming || mutation.isError ? 'text-danger-text' : 'text-muted hover:text-text',
      )}
      aria-label={confirming ? `Confirm delete ${campaign.name}` : `Delete ${campaign.name}`}
      title={mutation.isError ? 'The draft could not be deleted. Try again.' : 'Delete this draft'}
      disabled={mutation.isPending}
      onBlur={() => setConfirming(false)}
      onClick={() => (confirming ? mutation.mutate() : setConfirming(true))}
    >
      <Trash2 aria-hidden="true" className="size-3.5" />
      <span className="max-sm:hidden">
        {mutation.isPending ? 'Deleting…' : confirming ? 'Confirm' : 'Delete'}
      </span>
    </button>
  );
}

const ACTION_CLASS =
  'inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-xs font-medium text-muted hover:text-text hover:bg-card disabled:opacity-50';

function isArchivable(c: Campaign): boolean {
  return c.status === 'sent' || c.status === 'failed';
}

/**
 * Archives finished campaigns (hidden from the list, nothing deleted) or
 * brings them back. For a group it acts on every language version.
 */
function ArchiveButton({
  campaigns,
  label,
  restore,
}: {
  campaigns: Campaign[];
  label: string;
  restore: boolean;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      for (const c of campaigns) {
        await apiFetch(`/campaigns/${c.id}/archive`, {
          method: 'POST',
          body: { archived: !restore },
        });
      }
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });
  const Icon = restore ? ArchiveRestore : Archive;
  const verb = restore ? 'Restore' : 'Archive';
  return (
    <button
      type="button"
      className={cn(ACTION_CLASS, mutation.isError && 'text-danger-text')}
      aria-label={`${verb} ${label}`}
      title={
        mutation.isError
          ? `Could not ${verb.toLowerCase()} it. Try again.`
          : restore
            ? 'Show it in the list again'
            : 'Hide it from the list. Stats and unsubscribes are kept.'
      }
      disabled={mutation.isPending}
      onClick={() => mutation.mutate()}
    >
      <Icon aria-hidden="true" className="size-3.5" />
      <span className="max-sm:hidden">{verb}</span>
    </button>
  );
}

/** Same rule as the server (core campaignTags.repo.ts): lowercase letters, digits, æøå, hyphen. */
const CAMPAIGN_TAG_PATTERN = /^[a-z0-9æøå-]{1,40}$/;

/** Editor+: organising tags on one campaign (not audience rules). */
function CampaignTagsDialog({ campaign }: { campaign: Campaign }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const parsed = text
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const valid = parsed.length <= 10 && parsed.every((t) => CAMPAIGN_TAG_PATTERN.test(t));
  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/campaigns/${campaign.id}/tags`, { method: 'PUT', body: { tags: parsed } }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
        queryClient.invalidateQueries({ queryKey: ['campaign-tags'] }),
      ]);
      setOpen(false);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setText((campaign.tags ?? []).join(', '));
        else save.reset();
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          className={ACTION_CLASS}
          aria-label={`Tags for ${campaign.name}`}
          title="Organise with tags"
        >
          <Tag aria-hidden="true" className="size-3.5" />
          <span className="max-sm:hidden">Tags</span>
        </button>
      </DialogTrigger>
      <DialogContent
        title="Campaign tags"
        description="Tags only help you find campaigns. They do not change who receives them."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) save.mutate();
          }}
        >
          {save.isError ? (
            <Alert>
              {save.error instanceof ApiError && save.error.message
                ? save.error.message
                : 'The tags could not be saved. Please try again.'}
            </Alert>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="campaign-tags-input">Tags, separated by commas</Label>
            <Input
              id="campaign-tags-input"
              value={text}
              placeholder="e.g. launch, black-friday"
              autoComplete="off"
              onChange={(e) => setText(e.target.value)}
            />
            <span className="text-xs text-muted">
              Up to 10 tags: lowercase letters, digits, æøå and hyphens.
            </span>
          </div>
          <DialogFooter>
            <DialogClose>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!valid || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save tags'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface RowActions {
  isEditor: boolean;
  duplicate: (id: string) => void;
  duplicating: boolean;
}

function CampaignActions({ c, actions }: { c: Campaign; actions: RowActions }) {
  if (!actions.isEditor) return <span className="w-4 shrink-0" />;
  const archived = c.archivedAt != null;
  return (
    <span className="w-[244px] max-sm:w-[120px] shrink-0 flex justify-end gap-1 pr-3">
      <CampaignTagsDialog campaign={c} />
      {c.status === 'draft' ? <DeleteDraftButton campaign={c} /> : null}
      {isArchivable(c) ? <ArchiveButton campaigns={[c]} label={c.name} restore={archived} /> : null}
      {archived ? null : (
        <button
          type="button"
          className={ACTION_CLASS}
          aria-label={`Duplicate ${c.name}`}
          title="Duplicate into a new draft"
          disabled={actions.duplicating}
          onClick={() => actions.duplicate(c.id)}
        >
          <Copy aria-hidden="true" className="size-3.5" />
          <span className="max-sm:hidden">Duplicate</span>
        </button>
      )}
    </span>
  );
}

/** One campaign; inside a group it shows its language instead of the full name. */
function CampaignRow({
  c,
  actions,
  language,
}: {
  c: Campaign;
  actions: RowActions;
  language?: string;
}) {
  const note = sendTimeNote(c);
  return (
    <div
      className={cn(
        'flex items-center border-b border-border-soft last:border-b-0 hover:bg-bg',
        language && 'bg-bg/50',
      )}
    >
      <Link
        to={campaignHref(c)}
        className={cn(
          'flex flex-grow min-w-0 items-center pl-4 no-underline text-text',
          language ? 'py-2.5' : 'py-3.5',
        )}
      >
        <span className="w-[170px] max-sm:w-[112px] shrink-0">
          <CampaignStatusBadge status={c.status} />
        </span>
        <span className={cn('flex-grow min-w-0', language && 'pl-6')}>
          {language ? (
            <span className="block font-mono text-xs font-medium">
              <span aria-hidden="true">{language}</span>
              <span className="sr-only">{c.name}</span>
            </span>
          ) : (
            <span className="block font-medium">{c.name}</span>
          )}
          {note ? <span className="block text-xs text-muted">{note}</span> : null}
          {c.tags && c.tags.length > 0 ? (
            <span className="mt-1 flex flex-wrap gap-1" aria-label="Tags on this campaign">
              {c.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-border bg-card px-2 py-0.5 text-[11px] text-muted"
                >
                  {t}
                </span>
              ))}
            </span>
          ) : null}
        </span>
        <span className="w-[110px] max-sm:hidden shrink-0 pr-3 text-right text-muted">
          {formatDate(c.createdAt)}
        </span>
      </Link>
      <CampaignActions c={c} actions={actions} />
    </div>
  );
}

/**
 * "Duplicate" for a group: copies every language into new drafts under one
 * new name ("<name> (DA)", "<name> (DE)", ...), all or none, and opens the
 * new group's page. Like a single duplicate, the copies still need their
 * own test emails and approval.
 */
function DuplicateGroupDialog({ group }: { group: CampaignGroup }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const languages = group.members.map((m) => m.language);
  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<{ campaigns: Campaign[] }>('/campaigns/duplicate-group', {
        method: 'POST',
        body: { campaignIds: group.members.map((m) => m.campaign.id), name: name.trim() },
      }),
    onSuccess: ({ campaigns }) => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setOpen(false);
      navigate(`/kampagner/${campaigns[0]!.id}/send-all`);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setName(`${group.name} (copy)`);
          mutation.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          className={ACTION_CLASS}
          aria-label={`Duplicate all languages of ${group.name}`}
          title="Copy every language into new drafts"
        >
          <Copy aria-hidden="true" className="size-3.5" />
          <span className="max-sm:hidden">Duplicate</span>
        </button>
      </DialogTrigger>
      <DialogContent title={`Duplicate "${group.name}"`}>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <p className="m-0 text-sm text-muted">
            {`Copies all ${languages.length} languages (${languages.join(', ')}) into new drafts with their content, subject lines and audience. Each needs its own test email and approval.`}
          </p>
          {mutation.isError ? (
            <Alert>
              {mutation.error instanceof ApiError && mutation.error.body.message
                ? mutation.error.body.message
                : 'The languages could not be copied. Nothing was created.'}
            </Alert>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="group-copy-name">New name</Label>
            <Input
              id="group-copy-name"
              required
              maxLength={195}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <p className="m-0 text-xs text-muted">
              {name.trim()
                ? `The drafts are called ${languages.map((l) => `"${name.trim()} (${l})"`).join(', ')}.`
                : 'Without the language. It is added to each copy.'}
            </p>
          </div>
          <Button type="submit" disabled={!name.trim() || mutation.isPending}>
            {mutation.isPending ? 'Copying…' : `Copy ${languages.length} languages`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The language versions of one send: one row, unfolded to a row per language. */
function GroupRow({ group, actions }: { group: CampaignGroup; actions: RowActions }) {
  const [open, setOpen] = useState(false);
  const panelId = `campaign-group-${group.members[0]?.campaign.id ?? group.name}`;
  const campaigns = group.members.map((m) => m.campaign);
  const archived = campaigns.every((c) => c.archivedAt != null);
  // "Send all" reviews and approves the drafts together; it needs two of them.
  const drafts = campaigns.filter((c) => c.status === 'draft');
  return (
    <div className="border-b border-border-soft last:border-b-0">
      <div className="flex items-center hover:bg-bg">
        <div className="flex flex-grow min-w-0 items-center pl-4 py-3.5">
          <span className="w-[170px] max-sm:w-[112px] shrink-0">
            <CampaignGroupStatusBadge group={group} />
          </span>
          <span className="flex-grow min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <button
                type="button"
                aria-expanded={open}
                aria-controls={panelId}
                aria-label={`Languages of ${group.name}`}
                title={open ? 'Hide the languages' : 'Show the languages'}
                className="-m-1.5 p-1.5 shrink-0 rounded text-muted hover:text-text focus-visible:outline-2 focus-visible:outline-accent"
                onClick={() => setOpen((v) => !v)}
              >
                <ChevronRight
                  aria-hidden="true"
                  className={cn('size-4 transition-transform', open && 'rotate-90')}
                />
              </button>
              {/* The group's own page: every language side by side, approved together. */}
              <Link
                to={`/kampagner/${(drafts[0] ?? campaigns[0]!).id}/send-all`}
                className="min-w-0 font-medium text-text no-underline hover:text-link"
              >
                {group.name}
              </Link>
            </span>
            <span className="flex flex-wrap gap-1">
              {group.members.map((m) => (
                <Link
                  key={m.campaign.id}
                  to={campaignHref(m.campaign)}
                  aria-label={`Open ${m.campaign.name}`}
                  className="font-mono text-[11px] font-medium leading-none px-1.5 py-1 rounded border border-border text-muted no-underline hover:border-accent hover:text-link"
                >
                  {m.language}
                </Link>
              ))}
            </span>
          </span>
          <span className="w-[110px] max-sm:hidden shrink-0 pr-3 text-right text-muted">
            {formatDate(group.createdAt)}
          </span>
        </div>
        {actions.isEditor ? (
          <span className="w-[176px] max-sm:w-[88px] shrink-0 flex justify-end gap-1 pr-3">
            {drafts.length >= 2 ? (
              <Link
                to={`/kampagner/${drafts[0]!.id}/send-all`}
                className={cn(ACTION_CLASS, 'no-underline')}
                aria-label={`Send all languages of ${group.name}`}
                title="Review and approve every language together"
              >
                <Send aria-hidden="true" className="size-3.5" />
                <span className="max-sm:hidden">Send all</span>
              </Link>
            ) : null}
            {campaigns.every(isArchivable) ? (
              <ArchiveButton
                campaigns={campaigns}
                label={`all languages of ${group.name}`}
                restore={archived}
              />
            ) : null}
            {archived ? null : <DuplicateGroupDialog group={group} />}
          </span>
        ) : (
          <span className="w-4 shrink-0" />
        )}
      </div>
      <div id={panelId} hidden={!open} className="border-t border-border-soft">
        {group.members.map((m) => (
          <CampaignRow key={m.campaign.id} c={m.campaign} actions={actions} language={m.language} />
        ))}
      </div>
    </div>
  );
}

function CampaignTable({
  entries,
  actions,
}: {
  entries: CampaignListEntry[];
  actions: RowActions;
}) {
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <div className="flex items-center pl-4 py-3 border-b border-border-soft text-muted text-xs">
        <span className="w-[170px] max-sm:w-[112px] shrink-0">Status</span>
        <span className="flex-grow">Name</span>
        <span className="w-[110px] max-sm:hidden shrink-0 pr-3 text-right">Created</span>
        {actions.isEditor ? <span className="w-[176px] max-sm:w-[88px] shrink-0" /> : null}
      </div>
      {entries.map((e) =>
        e.kind === 'group' ? (
          <GroupRow key={`g:${e.group.name}`} group={e.group} actions={actions} />
        ) : (
          <CampaignRow key={e.campaign.id} c={e.campaign} actions={actions} />
        ),
      )}
    </div>
  );
}

export function KampagnerListPage() {
  const auth = useAuth();
  // A custom role (K8) can take campaign editing away; the server refuses it anyway.
  const isEditor = auth.isEditor && auth.can('campaigns.edit');
  const [searchParams, setSearchParams] = useSearchParams();
  const rawView = searchParams.get('view');
  const view: CampaignView = isCampaignView(rawView) ? rawView : 'liste';
  const tagFilter = searchParams.get('tag') ?? '';
  const { data, isLoading } = useQuery({
    queryKey: tagFilter ? ['campaigns', { tag: tagFilter }] : ['campaigns'],
    queryFn: () =>
      apiFetch<{ campaigns: Campaign[] }>(
        tagFilter ? `/campaigns?tag=${encodeURIComponent(tagFilter)}` : '/campaigns',
      ),
  });
  const tagList = useQuery({
    queryKey: ['campaign-tags'],
    queryFn: () => apiFetch<{ tags: CampaignTagCount[] }>('/campaigns/tags'),
  });
  const knownTags = tagList.data?.tags ?? [];
  const { duplicate, pending: duplicating, error: duplicateError } = useDuplicateCampaign();
  const all = [...(data?.campaigns ?? [])].sort((a, b) => b.createdAt - a.createdAt);
  // A flow step's mail is a campaign row too, but it belongs to its
  // automation and stays `sending` while the flow runs, so it is not listed
  // here. One that stopped (paused or failed) is shown, because this is
  // where it can be opened and resumed.
  const campaigns = all.filter((c) => c.kind !== 'flow_step');
  // Archived campaigns (sent or failed, hidden on purpose) are left out of
  // the list and the calendar, behind a "Show" line at the bottom.
  const listed = campaigns.filter((c) => c.archivedAt == null);
  const archived = campaigns.filter((c) => c.archivedAt != null);
  const [showArchived, setShowArchived] = useState(false);
  const actions: RowActions = { isEditor, duplicate, duplicating };
  const stoppedSteps = all.filter(
    (c) => c.kind === 'flow_step' && (c.status === 'paused' || c.status === 'failed'),
  );

  function setTag(tag: string): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tag) next.set('tag', tag);
        else next.delete('tag');
        return next;
      },
      { replace: true },
    );
  }

  function setView(v: CampaignView): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === 'liste') next.delete('view');
        else next.set('view', v);
        return next;
      },
      { replace: true },
    );
  }

  return (
    <>
      <PageHeader
        title="Campaigns"
        badge={<MockBadge />}
        description="Emails you send to your lists and segments."
        actions={
          <>
            {knownTags.length > 0 || tagFilter ? (
              <div className="flex items-center gap-2">
                <Label htmlFor="campaign-tag-filter" className="whitespace-nowrap">
                  Tag
                </Label>
                <select
                  id="campaign-tag-filter"
                  className="h-9 rounded-md border border-border bg-card px-2 text-sm"
                  value={tagFilter}
                  onChange={(e) => setTag(e.target.value)}
                >
                  <option value="">All campaigns</option>
                  {knownTags.map((t) => (
                    <option key={t.tag} value={t.tag}>
                      {t.tag} ({t.count})
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <ViewTabs view={view} onChange={setView} />
            {isEditor ? <CreateCampaignDialog /> : null}
          </>
        }
      />

      {stoppedSteps.length > 0 ? (
        <Card className="p-4 text-sm flex flex-col gap-1">
          <b>Automation mails that stopped</b>
          <span className="text-muted">
            These mails belong to an automation and were paused. Open one to see why and resume it.
          </span>
          <ul className="m-0 pl-5">
            {stoppedSteps.map((c) => (
              <li key={c.id}>
                <Link to={campaignHref(c)}>{c.name}</Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {isLoading ? (
        <div className="text-muted">Loading campaigns…</div>
      ) : campaigns.length === 0 ? (
        <EmptyState
          icon={<Send />}
          title="No campaigns yet"
          description="A campaign is one email sent to the lists or segments you choose. Start with a draft and send a test to yourself first."
          action={isEditor ? <CreateCampaignDialog /> : undefined}
        />
      ) : view === 'kalender' ? (
        <CampaignCalendar campaigns={listed} />
      ) : (
        <>
          {duplicateError ? <Alert>{duplicateError}</Alert> : null}
          {listed.length > 0 ? (
            <CampaignTable entries={groupCampaigns(listed)} actions={actions} />
          ) : (
            <p className="m-0 text-muted text-sm">Every campaign is archived.</p>
          )}
          {archived.length > 0 ? (
            <div className="flex flex-col gap-3">
              <p className="m-0 text-xs text-muted">
                {archived.length} archived ·{' '}
                <button
                  type="button"
                  className="text-link underline-offset-2 hover:underline"
                  aria-expanded={showArchived}
                  aria-controls="archived-campaigns"
                  onClick={() => setShowArchived((v) => !v)}
                >
                  {showArchived ? 'Hide' : 'Show'}
                </button>
              </p>
              <div id="archived-campaigns" hidden={!showArchived}>
                <CampaignTable entries={groupCampaigns(archived)} actions={actions} />
              </div>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
